/**
 * Light Hunt x Display Voices rewards (owner decisions 2026-10-05). One source
 * for the page, the API, and both emails, so the offer and the guarantees
 * read the same everywhere. Codes come from Display Voices' Stripe and are
 * assigned by supabase/migrations/20261006000000_voice_rewards.sql.
 */

export type VoiceRewardKey = 'fb_group' | 'display' | 'sequences' | 'voting';
export type VoiceRewardStatus = 'pending' | 'granted' | 'rejected';
export type VoiceRewardCodeKind = 'template' | 'custom';

export const FACEBOOK_GROUP_URL = 'https://www.facebook.com/groups/1635818076622832';
export const DISPLAY_VOICES_URL = 'https://displayvoices.com';
/** Display Voices' song voting reminder template, preselected. Change here if the template slug moves. */
export const VOTING_VOICE_TEMPLATE_URL = `${DISPLAY_VOICES_URL}/templates?template=christmas-song-voting-reminder`;
export const REQUIRED_LISTED_SEQUENCES = 4;
/** Codes are minted with this expiry in Display Voices' Stripe. */
export const VOICE_REWARD_CODES_EXPIRE = 'January 31, 2027';

export interface VoiceRewardDefinition {
  key: VoiceRewardKey;
  /** What to do, as the card heading. */
  action: string;
  /** What you get. */
  reward: string;
  valueCents: number;
  codeKind: VoiceRewardCodeKind;
  /** Where to spend the code on Display Voices. */
  redeemUrl: string;
  /** What the code buys, for the code email ("template voiceover" when absent). */
  noun?: string;
}

export const VOICE_REWARDS: readonly VoiceRewardDefinition[] = [
  {
    key: 'fb_group',
    action: 'Join the Light Hunt Facebook group',
    reward: 'A free template voiceover',
    valueCents: 1299,
    codeKind: 'template',
    redeemUrl: `${DISPLAY_VOICES_URL}/templates`,
  },
  {
    key: 'display',
    action: 'Add your display, this year or a past one',
    reward: 'A second free template voiceover',
    valueCents: 1299,
    codeKind: 'template',
    redeemUrl: `${DISPLAY_VOICES_URL}/templates`,
  },
  {
    key: 'sequences',
    action: `List ${REQUIRED_LISTED_SEQUENCES} sequences, free or paid`,
    reward: 'A free custom-script voiceover',
    valueCents: 2999,
    codeKind: 'custom',
    redeemUrl: `${DISPLAY_VOICES_URL}/custom-order`,
  },
] as const;

/**
 * The song voting reward. Kept out of VOICE_REWARDS on purpose: that list drives the /voices
 * page and the launch announcement, and this reward has its own page and email.
 */
export const VOTING_VOICE_REWARD: VoiceRewardDefinition = {
  key: 'voting',
  action: 'Turn on song voting on your display',
  reward: 'A free song voting announcement',
  valueCents: 1299,
  codeKind: 'template',
  redeemUrl: VOTING_VOICE_TEMPLATE_URL,
  noun: 'song voting announcement',
};

/** Display Voices' song voting reminder templates, one per season, with the character preselected. */
const VOTING_VOICE_SEASONS = {
  christmas: { template: 'christmas-song-voting-reminder', character: 'santa' },
  halloween: { template: 'halloween-song-voting-reminder', character: 'rattle-jack' },
} as const;

export type VotingVoiceSeason = keyof typeof VOTING_VOICE_SEASONS;

/** Display Voices character slugs for the Christmas announcement. The Show Announcer is Display Voices' `default`. */
export const VOTING_VOICE_CHARACTERS = {
  default: 'Show Announcer',
  santa: 'Santa',
} as const;

export type VotingVoiceCharacter = keyof typeof VOTING_VOICE_CHARACTERS;

/**
 * Order link for the voting announcement, with the show name filled in when we have one.
 * `character` picks the Christmas voice; Halloween always uses its own character.
 */
export function votingVoiceOrderUrl(
  season: VotingVoiceSeason,
  showName?: string | null,
  character?: VotingVoiceCharacter,
): string {
  const { template, character: seasonCharacter } = VOTING_VOICE_SEASONS[season];
  const slug = season === 'christmas' && character ? character : seasonCharacter;
  const show = showName?.trim() ? `&show=${encodeURIComponent(showName.trim())}` : '';
  return `${DISPLAY_VOICES_URL}/templates?template=${template}&character=${slug}${show}`;
}

export function voiceRewardDefinition(key: VoiceRewardKey): VoiceRewardDefinition {
  const definition = [...VOICE_REWARDS, VOTING_VOICE_REWARD].find((reward) => reward.key === key);
  if (!definition) throw new Error(`Unknown voice reward: ${key}`);
  return definition;
}

export function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Display Voices' promises, owner-confirmed 2026-10-05. */
export const VOICE_GUARANTEES = [
  {
    title: 'Love it or your money back',
    body: "If the voiceover isn't right for your show, tell us and we refund you in full. No forms, no arguing.",
  },
  {
    title: 'Unlimited revisions',
    body: 'Wrong pronunciation, wrong pace, wrong name? Reply to your delivery email and we redo it, as many times as it takes, at no charge.',
  },
  {
    title: 'Delivered within 24 hours',
    body: 'Most voiceovers are ready in minutes. Every order is guaranteed within 24 hours of checkout.',
  },
] as const;

export const VOICE_GUARANTEE_LINE = '100% satisfaction guarantee · Unlimited revisions · Ready within 24 hours';

/** Shape returned by GET /api/rewards/voice (and get_voice_reward_progress). */
export interface VoiceRewardClaim {
  reward: VoiceRewardKey;
  status: VoiceRewardStatus;
  code: string | null;
  value_cents: number | null;
  created_at: string;
}

export type VotingPath = 'lor' | 'remote_falcon';

export interface VoiceRewardProgress {
  eligible: boolean;
  has_display: boolean;
  listed_sequences: number;
  claims: VoiceRewardClaim[];
  /**
   * Song voting fields, from migration 20261106100000. They describe the owner's best display (a ready one,
   * else the closest to ready) and are absent until the migration is applied.
   */
  voting_ready?: boolean;
  voting_path?: VotingPath | null;
  voting_display_id?: number | null;
  voting_display_name?: string | null;
  has_relay_pairing?: boolean;
  has_fpp_pairing?: boolean;
  has_remote_falcon?: boolean;
  /** Song voting switched on for the Light-O-Rama Relay. */
  lor_voting_enabled?: boolean;
  /** The Relay reports LOR Advanced, which cannot run voting. */
  lor_advanced?: boolean;
  /** The Remote Falcon voting widget switched on for the display page. */
  remote_falcon_voting_enabled?: boolean;
}
